import React, { useEffect } from "react";

import { LoadingIcon, Typography, Modal } from "@packages/ds";
import { useIntl } from "react-intl";

import Button, { ButtonWrapper } from "ds/Button";
import {
  getOrgSubscriptionEstimate,
  orgSubscriptionEstimateSelector,
  selectSubscriptionDetails
} from "Reducers/organization/subscription";
import { calculateAppAndServices } from "src/organization/pages/project/environment/ConfigureResourcesPage/util";
import { useAppSelector, useAppDispatch } from "Store/hooks";

import * as S from "./AllocateGuaranteedResourcesModal.styles";

import type { ResourceData } from "src/organization/pages/project/environment/ConfigureResourcesPage/types";

type Props = {
  isOpen: boolean;
  formState: ResourceData[];
  organizationId: string;
  subscriptionId: string;
  closeModal: () => void;
  onSave: () => void;
};

const AllocateGuaranteedResourcesModal = ({
  isOpen,
  formState,
  organizationId,
  subscriptionId,
  closeModal,
  onSave
}: Props) => {
  const intl = useIntl();
  const dispatch = useAppDispatch();

  const { guaranteedCpuApp, guaranteedCpuServices } =
    calculateAppAndServices(formState);

  const subscription = useAppSelector(state =>
    selectSubscriptionDetails(state, {
      organizationId,
      subscriptionId
    })
  );

  const { data: estimate, loading: loadingEstimate } = useAppSelector(state =>
    orgSubscriptionEstimateSelector(state, {
      organizationId
    })
  );

  useEffect(() => {
    const getSubscriptionEstimate = async () => {
      await dispatch(
        getOrgSubscriptionEstimate({
          subscription: subscription!,
          organizationId,
          parameters: {
            plan: "upsun/flexible",
            guaranteed_cpu_app: guaranteedCpuApp,
            guaranteed_cpu_services: guaranteedCpuServices
          }
        })
      );
    };
    if (subscription) {
      getSubscriptionEstimate();
    }
  }, [
    subscription,
    guaranteedCpuApp,
    guaranteedCpuServices,
    organizationId,
    dispatch
  ]);

  return (
    <>
      <Modal.Overlay />
      <Modal size="medium" isOpen={isOpen} onOpenChange={closeModal}>
        <Typography.Title tag="h3">
          {intl.formatMessage({
            id: "configure_resources.guaranteed.modal.title",
            defaultMessage: "Allocate guaranteed resources?"
          })}
        </Typography.Title>
        <S.ContentWrapper>
          <Typography.BodyText>
            {intl.formatMessage(
              {
                id: "configure_resources.guaranteed.modal.description1"
              },
              {
                link: chunks => (
                  <S.Link
                    analyticId="links.pricing.guaranteed_resources.modal"
                    href={intl.formatMessage({
                      id: "links.pricing"
                    })}
                  >
                    {chunks}
                  </S.Link>
                )
              }
            )}
          </Typography.BodyText>
          {!loadingEstimate &&
            estimate?.guaranteed_cpu_app &&
            estimate?.guaranteed_cpu_services && (
              <div>
                <Typography.BodyText weight="semiBold">
                  {intl.formatMessage(
                    {
                      id: "configure_resources.guaranteed.modal.app.cpu"
                    },
                    {
                      price: estimate?.guaranteed_cpu_app as string
                    }
                  )}
                </Typography.BodyText>
                <Typography.BodyText weight="semiBold">
                  {intl.formatMessage(
                    {
                      id: "configure_resources.guaranteed.modal.service.cpu"
                    },
                    {
                      price: estimate?.guaranteed_cpu_services as string
                    }
                  )}
                </Typography.BodyText>
              </div>
            )}
          <Typography.BodyText>
            {intl.formatMessage({
              id: "configure_resources.guaranteed.modal.description2"
            })}
          </Typography.BodyText>
        </S.ContentWrapper>

        <ButtonWrapper justifyContent="end" spacing="modal">
          {loadingEstimate ? (
            <LoadingIcon />
          ) : (
            <>
              <Button variant="secondary" onClick={closeModal}>
                {intl.formatMessage({ id: "cancel" })}
              </Button>
              <Button onClick={onSave}>
                {intl.formatMessage({
                  id: "configure_resources.guaranteed.modal.continue",
                  defaultMessage: "Yes, allocate"
                })}
              </Button>
            </>
          )}
        </ButtonWrapper>
      </Modal>
    </>
  );
};

export default AllocateGuaranteedResourcesModal;
